"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.aiAdvisorAccess = aiAdvisorAccess;
exports.appliedLabels = appliedLabels;
exports.bgpStatus = bgpStatus;
exports.credentials = credentials;
exports.deviceClass = deviceClass;
exports.deviceType = deviceType;
exports.effectiveMonitoringTemplateId = effectiveMonitoringTemplateId;
exports.flowConfigured = flowConfigured;
exports.flowSource = flowSource;
exports.flowType = flowType;
exports.hasCommandAccess = hasCommandAccess;
exports.hasConfigFetch = hasConfigFetch;
exports.hasKentikAgent = hasKentikAgent;
exports.hasKproxyAgent = hasKproxyAgent;
exports.hasStreamingTelemetry = hasStreamingTelemetry;
exports.icmpStatus = icmpStatus;
exports.isEnrichingFlowWithUA = isEnrichingFlowWithUA;
exports.isICMP = isICMP;
exports.isICMPOnly = isICMPOnly;
exports.isNMS = isNMS;
exports.nmsConfigured = nmsConfigured;
exports.percentClassified = percentClassified;
exports.rawFps = rawFps;
exports.sampleRate = sampleRate;
exports.sendingIpSortValue = sendingIpSortValue;
exports.snmpEnrichmentStatus = snmpEnrichmentStatus;
exports.snmpNmsStatus = snmpNmsStatus;
exports.snmpStatus = snmpStatus;
exports.stEnabled = stEnabled;
exports.stEnrichmentStatus = stEnrichmentStatus;
exports.stNmsStatus = stNmsStatus;
exports.statusDisplay = statusDisplay;
var _ipAddress = require("ip-address");
var _deviceMetadata = require("@kentik/ui-shared/nms/deviceMetadata");
// Derived device fields shared between UI (DeviceModel) and Node (getDeviceTableData).
// Keeps both sides in sync to allow predictable display & filtering behavior.
// UI passes DeviceModel's observable `attributes`; Node passes a plain JSON row.
// These must be pure functions.

function includesKentikAgent(value) {
  if (typeof value === 'string') {
    return value.includes('kentik_agent');
  }
  if (Array.isArray(value)) {
    return value.includes('kentik_agent');
  }
  return false;
}
const DEVICE_TYPE_ACRONYMS = {
  router: 'Router',
  host: 'cHst',
  'host-nprobe-basic': 'nHst',
  'host-nprobe-dns-www': 'KPb'
};
function deviceType(fields) {
  return fields.device_subtype || DEVICE_TYPE_ACRONYMS[fields.device_type];
}
function rawFps(fields) {
  const fps = Number(fields.raw_fps);
  return Number.isNaN(fps) ? 0 : fps;
}
function flowSource(fields) {
  if (!rawFps(fields)) {
    return 'none';
  }
  const stats = fields.stats || {};
  const isAgent = parseInt(String(stats.IsAgent), 10);
  if (!isAgent) {
    return 'client';
  }
  return isAgent === 1 ? 'agent' : 'none';
}
function flowType(fields) {
  const stats = fields.stats || {};
  return stats.FlowType;
}

// Not shared. Row's `labels` already carries resolved {id, name, color} objects,
// while DeviceModel looks names up via the $labels store.
function appliedLabels(fields) {
  const labels = fields.labels || [];
  return labels.length ? labels.map(label => label.name).join(' ') : '';
}
function isICMP(fields) {
  return !!fields.ranger_icmp_ip;
}
function flowConfigured(fields) {
  const sendingIps = Array.isArray(fields.sending_ips) ? fields.sending_ips : [];
  return sendingIps.length > 0 && !!fields.device_sample_rate;
}
function isICMPOnly(fields) {
  return isICMP(fields) && !flowConfigured(fields);
}
function isNMS(fields) {
  return includesKentikAgent(fields.snmp_collection_method);
}
function nmsConfigured(fields) {
  return isNMS(fields) || isICMP(fields);
}
function isEnrichingFlowWithUA(fields) {
  const value = fields.snmp_collection_method;
  if (typeof value === 'string') {
    return value.includes('kentik_agent_flow_only');
  }
  if (Array.isArray(value)) {
    return value.includes('kentik_agent_flow_only');
  }
  return false;
}
function effectiveMonitoringTemplateId(fields, defaultTemplateId) {
  const id = fields.monitoring_template_id;
  if (!isNMS(fields) || isEnrichingFlowWithUA(fields) || id) {
    return id;
  }
  return defaultTemplateId;
}
function deviceClass(fields) {
  const classification = fields.class;
  if (isICMPOnly(fields)) {
    return _deviceMetadata.AllDeviceClass.Icmp;
  }
  if (isICMP(fields)) {
    return _deviceMetadata.AllDeviceClass.FlowIcmp;
  }
  if (!isNMS(fields)) {
    return _deviceMetadata.AllDeviceClass.Flow;
  }
  if (!classification) {
    // Default class for unclassified NMS devices
    return _deviceMetadata.DEFAULT_DEVICE_CLASS;
  }
  if (!Object.values(_deviceMetadata.AllDeviceClass).includes(classification)) {
    // Classification isn't one the UI knows how to display
    return null;
  }
  return classification;
}
function hasStreamingTelemetry(fields) {
  return includesKentikAgent(fields.streaming_telemetry_collection_method);
}
function stEnabled(fields) {
  const conf = fields.device_gnmi_v1_conf;
  return !!conf && conf.dialoutServer === 'auto';
}
function hasKentikAgent(fields) {
  return !!fields.device_kentik_agent_id;
}
function hasKproxyAgent(fields) {
  return !!fields.device_kproxy_agent_id;
}
function snmpStatus(fields) {
  return Boolean(fields.snmp_enabled) && Boolean(fields.snmp_healthy);
}
function snmpEnrichmentStatus(fields) {
  return isICMPOnly(fields) ? 'unknown' : snmpStatus(fields);
}
function stEnrichmentStatus(fields) {
  if (!stEnabled(fields) || isICMPOnly(fields)) {
    return 'unknown';
  }
  return fields.st_healthy ? 'up' : 'down';
}
function snmpNmsStatus(fields) {
  if (!isNMS(fields) || isICMPOnly(fields)) {
    return 'unknown';
  }
  return fields.nms_status;
}
function stNmsStatus(fields) {
  if (!isNMS(fields) || isICMPOnly(fields)) {
    return 'unknown';
  }
  return hasStreamingTelemetry(fields) ? 'up' : 'down';
}
function icmpStatus(fields) {
  return isICMP(fields) ? fields.nms_status : 'unknown';
}

// Ordering-only, not a display value — IPv4/IPv6 sort numerically instead of lexicographically.
function sendingIpSortValue(fields) {
  const firstIp = fields.sending_ips?.[0];
  const IPV4_SORT_DIGITS = 10;
  const IPV6_SORT_DIGITS = 39;
  if (!firstIp) {
    return undefined;
  }
  try {
    const v4 = new _ipAddress.Address4(firstIp.trim());
    if (v4.isCorrect()) {
      return v4.bigInteger().toString().padStart(IPV4_SORT_DIGITS, '0');
    }
  } catch {
    // fall through to v6
  }
  try {
    // ip-address v6 is very strict with its correctness check; treat any successful parse as valid.
    const v6 = new _ipAddress.Address6(firstIp.trim());
    return v6.bigInteger().toString().padStart(IPV6_SORT_DIGITS, '0');
  } catch {
    return undefined;
  }
}
function statusDisplay(fields) {
  const status = fields.nms_status;
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
}
function bgpStatus(fields) {
  const stats = fields.stats || {};
  const metadata = fields.metadata || {};
  if (fields.device_bgp_type === 'device') {
    return {
      v4: {
        established: parseInt(String(stats.RibSessionState), 10) === 1,
        configured: !!metadata.device_bgp_neighbor_ip
      },
      v6: {
        established: parseInt(String(stats.RibSessionState6), 10) === 1,
        configured: !!metadata.device_bgp_neighbor_ip6
      }
    };
  }
  if (fields.device_bgp_type === 'other_device') {
    return {
      v4: {
        established: true,
        configured: true
      },
      v6: {
        established: true,
        configured: true
      }
    };
  }
  return {
    v4: {
      established: false,
      configured: false
    },
    v6: {
      established: false,
      configured: false
    }
  };
}
function percentClassified(fields) {
  const ic = fields.interface_classification;
  if (!ic) {
    return 0;
  }
  const classified = Number(ic.interfacesClassifiedCount ?? 0);
  const total = Number(ic.interfaceCount ?? 0);
  return total === 0 ? 0 : Math.round(classified / total * 100);
}
function sampleRate(fields) {
  const stats = fields.stats || {};
  // Number(null) is 0, not NaN — must reject null explicitly or it'd be mistaken for real data.
  if (stats.sampleRate === null || stats.sampleRate === undefined) {
    return null;
  }
  const rate = Number(stats.sampleRate);
  return Number.isFinite(rate) ? rate : null;
}
function credentials(fields, hasCommandAccessValue) {
  const result = [];
  if (fields.device_status !== 'V') {
    return result;
  }
  if (isNMS(fields)) {
    const options = fields.kentik_agent_snmp_options;
    const name = options?.credential_name ?? fields.agent_snmp_credential_name;
    if (name) {
      result.push(name);
    }
  }
  if (hasStreamingTelemetry(fields)) {
    const options = fields.streaming_telemetry_collection_options;
    const name = options?.credential_name ?? fields.agent_st_credential_name;
    if (name) {
      result.push(name);
    }
  }
  if (hasCommandAccessValue) {
    const options = fields.deviceconf_options;
    const name = options?.credential_name ?? fields.deviceconf_credential_name;
    if (name) {
      result.push(name);
    }
  }
  return result;
}
function hasCommandAccess(fields, hasPermission) {
  const options = fields.deviceconf_options;
  return hasPermission && (!!options?.enabled || !!fields.deviceconf_enabled || !!options?.agent_id || !!fields.deviceconf_agent_id);
}
function hasConfigFetch(fields, hasCommandAccessValue) {
  const options = fields.deviceconf_options;
  return hasCommandAccessValue && (!!options?.fetch_enabled || !!fields.deviceconf_fetch_enabled || !!options?.fetch_interval || !!fields.deviceconf_fetch_interval);
}

// `options != null` (not `?.`/`??`) is deliberate: a present-but-empty deviceconf_options object
// should NOT fall back to the flat field just because command_enabled happens to be unset on it.
function aiAdvisorAccess(fields, hasCommandAccessValue) {
  if (!hasCommandAccessValue) {
    return false;
  }
  const options = fields.deviceconf_options;
  const commandEnabled = options != null ? options.command_enabled : fields.deviceconf_command_enabled;
  return commandEnabled === true;
}